import { useState } from "react";
import moment from "moment-timezone";
import { Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGenerateDocumentDispatchEversignStatusReport } from "@/lib/api/otcDocuments";
import { downloadFromS3 } from "@/lib/file";
import {
  DocumentDispatchEversignStatusReportRequestValidation,
  type DocumentDispatchEversignStatusReportRequest,
} from "@/lib/validation/otcDocuments";
import { CommonDestinationTimezone } from "@/lib/utils";

type ReportFilters = Omit<
  DocumentDispatchEversignStatusReportRequest,
  "feature_type" | "visibility_status" | "date_field"
> & {
  date_field: DocumentDispatchEversignStatusReportRequest["date_field"] | "";
};

const emptyReportFilters: ReportFilters = { date_field: "sent_at" };

type ReportDialogProps = {
  featureType: DocumentDispatchEversignStatusReportRequest["feature_type"];
  title: string;
  triggerLabel: string;
};

const dateFieldOptions: Array<{
  value: DocumentDispatchEversignStatusReportRequest["date_field"];
  label: string;
}> = [
  { value: "sent_at", label: "Sent Date" },
  { value: "signed_at", label: "Signed Date" },
];

const queueStatusOptions: Array<{
  value: NonNullable<ReportFilters["queue_status"]>;
  label: string;
}> = [
  { value: "not_queued", label: "Not Sent" },
  { value: "submitted", label: "Submitted" },
  { value: "failed", label: "Failed" },
];

function reportFilename(): string {
  return `Contracts_Eversign_Status_${moment
    .tz(CommonDestinationTimezone)
    .format("YYYY_MM_DD")}.xlsx`;
}

export function DocumentDispatchEversignStatusReportDialog({
  featureType,
  title,
  triggerLabel,
}: ReportDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<ReportFilters>(emptyReportFilters);
  const [link, setLink] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const generateReport = useGenerateDocumentDispatchEversignStatusReport();

  const downloadReport = async (reportLink: string): Promise<void> => {
    const result = await downloadFromS3(reportLink, reportFilename());
    if (!result.success) {
      setErrorMessage("The report was generated, but the automatic download failed. Use Download Report to try again.");
    }
  };

  const onGenerate = async (): Promise<void> => {
    if (generateReport.isLoading) return;

    setErrorMessage(null);
    setLink(null);
    const parsed = DocumentDispatchEversignStatusReportRequestValidation.safeParse({
      feature_type: featureType,
      visibility_status: "visible",
      ...filters,
    });
    if (!parsed.success) {
      setErrorMessage(parsed.error.issues[0]?.message ?? "Please correct the report filters.");
      return;
    }

    try {
      const result = await generateReport.mutateAsync(parsed.data);
      if (!result.success) {
        setErrorMessage(result.message || "Failed to generate the report.");
        return;
      }

      setLink(result.data.link);
      await downloadReport(result.data.link);
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to generate the report.");
    }
  };

  const onOpenChange = (open: boolean): void => {
    if (!open && generateReport.isLoading) return;
    setIsOpen(open);
    if (!open) {
      setFilters(emptyReportFilters);
      setLink(null);
      setErrorMessage(null);
      generateReport.reset();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Select optional filters. Hidden documents are always excluded.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label>Date Filter</Label>
            <Combobox
              required
              dwimOnChange
              isModal
              placeholder="Select sent or signed date"
              options={dateFieldOptions}
              value={filters.date_field}
              onValueChange={(value: string) => setFilters((previous: ReportFilters): ReportFilters => ({
                ...previous,
                date_field: value as ReportFilters["date_field"],
              }))}
            />
          </div>
          <div />
          <div className="grid gap-2">
            <Label htmlFor="document-dispatch-report-after-date">From Date</Label>
            <Input
              id="document-dispatch-report-after-date"
              type="date"
              value={filters.after_date ?? ""}
              max={filters.before_date}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilters(
                (previous: ReportFilters): ReportFilters => ({
                  ...previous,
                  after_date: event.target.value || undefined,
                }),
              )}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="document-dispatch-report-before-date">To Date</Label>
            <Input
              id="document-dispatch-report-before-date"
              type="date"
              value={filters.before_date ?? ""}
              min={filters.after_date}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilters(
                (previous: ReportFilters): ReportFilters => ({
                  ...previous,
                  before_date: event.target.value || undefined,
                }),
              )}
            />
          </div>
          <div className="grid gap-2">
            <Label>Sent Status</Label>
            <Combobox
              dwimOnChange
              isModal
              placeholder="All"
              options={queueStatusOptions}
              value={filters.queue_status ?? ""}
              onValueChange={(value: string) => setFilters((previous: ReportFilters): ReportFilters => ({
                ...previous,
                queue_status: value ? value as ReportFilters["queue_status"] : undefined,
              }))}
            />
          </div>
        </div>

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter>
          <Button
            variant="outline"
            disabled={generateReport.isLoading}
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {link && (
            <Button
              variant="outline"
              disabled={generateReport.isLoading}
              onClick={() => downloadReport(link)}
            >
              <Download className="mr-2 h-4 w-4" /> Download Report
            </Button>
          )}
          <Button disabled={generateReport.isLoading || !filters.date_field} onClick={onGenerate}>
            {generateReport.isLoading ? "Generating..." : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
